import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { i18n } from '@/i18n';
import { isPlayerAccessDenied, playerAccessDeniedArea } from './playerAccessError';
import { mutationErrorMessage, shouldToastMutationError } from './mutationErrorToast';
import { defaultQueryRetry } from './queryRetry';

const DENIED_KEYS = {
  stats: 'team:playerAccess.deniedStats',
  video: 'team:playerAccess.deniedVideo',
} as const;

// The backend message is French-only: show a translated one, once per burst of failing queries.
function notifyPlayerAccessDenied(error: unknown): void {
  if (!isPlayerAccessDenied(error)) return;
  const area = playerAccessDeniedArea(error);
  toast.error(i18n.t(area ? DENIED_KEYS[area] : 'team:playerAccess.denied'), {
    id: 'player-access-denied',
  });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // A screen that explains the closed access itself opts out, or the user reads it twice.
    onError: (error, query) => {
      if (query.meta?.showsPlayerAccessDenied === true) return;
      notifyPlayerAccessDenied(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      notifyPlayerAccessDenied(error);
      if (shouldToastMutationError(error, mutation)) toast.error(mutationErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: defaultQueryRetry,
      refetchOnWindowFocus: false,
    },
  },
});
